import React from 'react';
import { Building, Calendar, MessageCircle, Briefcase, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const ContactCard = ({ contact, onClick, backgroundColor  }) => {
  const styles = {
    card: {
      background: backgroundColor || '#ffffff',
      borderRadius: '16px',
      padding: '1.5rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      border: '1px solid #e2e8f0',
      cursor: 'pointer',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    cardContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem',
      marginBottom: '1rem',
    },
    importanceBadge: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      padding: '0.375rem 0.75rem',
      borderRadius: '8px',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    avatar: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '1.5rem',
      color: 'white',
      flexShrink: 0,
    },
    info: {
      flex: 1,
      paddingRight: '80px', // Add space for the importance badge
    },
    name: {
      fontSize: '1.25rem',
      fontWeight: 700,
      fontFamily: "'Syne', sans-serif",
      marginBottom: '0.25rem',
      color: '#0f172a',
    },
    role: {
      color: '#475569',
      fontSize: '0.9rem',
      marginBottom: '0.25rem',
    },
    company: {
      color: '#2563eb',
      fontSize: '0.85rem',
      fontWeight: 600,
    },
    meta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.75rem',
      marginBottom: '1rem',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.375rem 0.75rem',
      background: '#f8fafc',
      borderRadius: '8px',
      fontSize: '0.8rem',
      color: '#475569',
    },
    notes: {
      color: '#475569',
      fontSize: '0.9rem',
      lineHeight: 1.5,
      marginBottom: '1rem',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    footer: {
      paddingTop: '1rem',
      borderTop: '1px solid #e2e8f0',
      marginTop: '.5rem',
    },
    footerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
    },
    interactions: {
      fontSize: '0.85rem',
      color: '#475569',
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
    },
    date: {
      fontSize: '0.85rem',
      color: '#94a3b8',
    },
    timestamp: {
      fontSize: '0.75rem',
      color: '#94a3b8',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      fontStyle: 'italic',
    },
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get the relevant date - use last_contacted if available, otherwise created_at
  const getRelevantDate = () => {
    return contact.last_contacted || contact.created_at;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'No date available';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimestampLabel = () => {
    // If no last_contacted but has created_at, show "Contact created at @"
    if (!contact.last_contacted && contact.created_at) {
      return 'Contact created at @';
    }
    // If has last_contacted, show "Last contacted at @"
    return 'Last contacted at @';
  };

  const getImportanceStyle = (importance) => {
    const importanceMap = {
      critical: {
        background: 'rgba(239, 68, 68, 0.15)',
        color: '#dc2626',
        border: '1px solid rgba(239, 68, 68, 0.3)',
      },
      high: {
        background: 'rgba(249, 115, 22, 0.15)',
        color: '#ea580c',
        border: '1px solid rgba(249, 115, 22, 0.3)',
      },
      medium: {
        background: 'rgba(59, 130, 246, 0.15)',
        color: '#2563eb',
        border: '1px solid rgba(59, 130, 246, 0.3)',
      },
      low: {
        background: 'rgba(107, 114, 128, 0.15)',
        color: '#6b7280',
        border: '1px solid rgba(107, 114, 128, 0.3)',
      },
    };

    const key = importance?.toLowerCase();
    return importanceMap[key] || importanceMap.low;
  };

  const relevantDate = getRelevantDate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }}
      style={styles.card}
      onClick={onClick}
    >
      {/* Importance Badge */}
      {contact.contact_importance && (
        <div style={{
          ...styles.importanceBadge,
          ...getImportanceStyle(contact.contact_importance)
        }}>
          {contact.contact_importance}
        </div>
      )}

      <div style={styles.cardContent}>
        <div style={styles.header}>
          <div style={styles.avatar}>{getInitials(contact.name)}</div>
          <div style={styles.info}>
            <h3 style={styles.name}>{contact.name}</h3>
            {contact.role && <p style={styles.role}>{contact.role}</p>}
            {contact.company && <p style={styles.company}>{contact.company}</p>}
          </div>
        </div>

        <div style={styles.meta}>
          {contact.connection_origin && (
            <span style={styles.badge}>
              <Briefcase size={14} />
              {contact.connection_origin}
            </span>
          )}
          {contact.interests && (
            <span style={styles.badge}>
              💡 {contact.interests.split(',')[0]}
            </span>
          )}
        </div>

        {contact.conversation_notes && (
          <p style={styles.notes}>{contact.conversation_notes}</p>
        )}
      </div>

      <div style={styles.footer}>
        <div style={styles.footerRow}>
          <span style={styles.interactions}>
            <MessageCircle size={14} />
            {contact.interaction_count || 0} interactions
          </span>
          <span style={styles.date}>
            <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
            {formatDate(contact.last_contacted)}
          </span>
        </div>
        <div style={styles.timestamp}>
          <Clock size={12} />
          {getTimestampLabel()} {formatDateTime(relevantDate)}
        </div>
      </div>
    </motion.div>
  );
};

export default ContactCard;